import React, { useEffect, useState } from 'react';
import { filter } from 'lodash';
import {
  Card,
  Stack,
  Avatar,
  Button,
  Checkbox,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  LinearProgress,
  Chip
} from '@material-ui/core';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { Label } from '@material-ui/icons';
import Page from '../../components/Page';
import Scrollbar from '../../components/Scrollbar';
import SearchNotFound from '../../components/SearchNotFound';
import { UserListHead, UserListToolbar, UserMoreMenu } from '../../components/_dashboard/user';
import { fDate, fDateTime } from '../../utils/formatTime';
import { fCurrency } from '../../utils/formatNumber';
import {
  getVendorsAction,
  logoutVendorAction,
  resetVendorPasswordAction,
  searchVendorsAction
} from '../../store/actions/VendorsActions';
import MoreMenu from '../../components/MoreMenu';
import UpdateVendor from './components/UpdateVendor';
import ViewPasswordInfo from './components/ViewPasswordResetInfo';
import TopupVendor from './components/TopupVendor';
import Table from '../../components/table';
import FilterVendors from './components/FilterVendors';

function VendorsModule() {
  const dispatch = useDispatch();
  const LIMIT = 50;
  const { isLoading, VENDORS_LIST } = useSelector((state) => state.vendors);
  const [loading, setLoading] = useState(false);

  const [filterName, setFilterName] = useState('');
  const [debounceTimer, setDebounceTimer] = useState('');

  const [currentRow, setCurrentRow] = useState(null);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isTopupVendorOpen, setIsTopupVendorOpen] = useState(false);
  const [selectedVendorID, setSelectedVendorID] = useState(null);
  const [showPwdInfo, setShowPwdInfo] = useState(false);

  const [dataPageFetched, setDataPageFetched] = useState(1);

  useEffect(() => {
    dispatch(getVendorsAction({ page: 1, limit: LIMIT }));
  }, []);

  useEffect(() => {
    if (debounceTimer) clearTimeout(debounceTimer);

    const timer = setTimeout(() => {
      dispatch(getVendorsAction({ searchName: filterName, page: 1, limit: LIMIT }));
    }, [1000]);
    setDebounceTimer(timer);
  }, [filterName]);

  const handleAsyncFilterByName = (name) => {
    setFilterName(name);
    setDataPageFetched(1);
  };

  const handleViewUpdateModal = (row) => {
    setIsUpdateModalOpen(!isUpdateModalOpen);
    setCurrentRow(row);
  };

  const handleCloseUpdateModal = () => {
    setIsUpdateModalOpen(false);
  };

  const handleShowPwdInfo = (message) => {
    setShowPwdInfo(!showPwdInfo);
    setCurrentRow(message);
  };

  const handleClosePwdInfoModal = () => {
    setShowPwdInfo(false);
  };

  const handleResetVendorPassword = async (id) => {
    const action = window.confirm(`Are you sure you want to Reset this Vendor Password`);
    if (action) {
      const response = await dispatch(resetVendorPasswordAction(id));
      if (response?.error === 1) {
        toast.error(`${response.message}`);
      } else handleShowPwdInfo(response);
    }
  };

  const handleLogoutVendor = async (id) => {
    const action = window.confirm(`Are you sure you want to Logout this Vendor`);
    if (action) {
      const response = await dispatch(logoutVendorAction(id));
      if (response?.error === 1) {
        toast.error(`${response.message}`);
      }
    }
  };

  const handleTopupVendor = (id) => {
    setIsTopupVendorOpen(!isUpdateModalOpen);
    setSelectedVendorID(id);
  };
  const handleCloseTopupVendorModal = () => {
    setIsTopupVendorOpen(false);
  };

  const TABLE_HEAD = [
    { id: 'id', label: 'S/N', render: (index, row) => index + 1 },
    {
      id: 'fullname',
      label: 'Name',
      align: 'left'
    },
    {
      id: 'phone',
      label: 'Phone',
      align: 'left'
    },
    {
      id: 'email',
      label: 'Email',
      align: 'left'
    },
    {
      id: 'accessStatus',
      label: 'Status',
      align: 'left',
      render: (index, rowData) => (
        <Chip
          label={rowData.accessStatus == 1 ? 'Active' : 'Inactive'}
          color={rowData.accessStatus == 1 ? 'primary' : 'error'}
        />
      )
    },
    {
      id: 'phoneVerified',
      label: 'Phone Verified',
      align: 'left',
      render: (index, rowData) => (
        <Chip
          label={rowData.phoneVerified == 1 ? 'Verified' : 'Unverified'}
          color={rowData.phoneVerified == 1 ? 'primary' : 'error'}
        />
      )
    },
    {
      id: 'emailVerified',
      label: 'Email Verified',
      align: 'left',
      render: (index, rowData) => (
        <Chip
          label={rowData.emailVerified == 1 ? 'Verified' : 'Unverified'}
          color={rowData.emailVerified == 1 ? 'primary' : 'error'}
        />
      )
    },
    {
      id: 'vendor_feature',
      label: 'Current Daily Cost',
      align: 'left',
      render: (index, rowData) => rowData?.vendor_feature && fCurrency(rowData?.vendor_feature?.currDailyCost || 0)

    },
    {
      id: 'referredBy',
      label: 'Referred By',
      align: 'left',
      render: (index, rowData) => (
        <>
          {rowData.referredBy || 'No one'}
          {rowData.referredBy && rowData?.parent_referral?.fullname}
        </>
      )
    },
    {
      id: 'childrenReferralsCount',
      label: 'Children Referrals Count',
      align: 'left',
      render: (index, rowData) => rowData.childrenReferralsCount || 0
    },
    {
      id: 'dateCreated',
      label: 'Date Created',
      align: 'left',
      render: (index, rowData) => fDate(rowData.dateCreated)
    },
    {
      id: 'action',
      label: 'Actions',
      align: 'left',
      render: (index, rowData) => (
        <MoreMenu
          topup
          edit
          reset
          logout
          handleLogout={() => handleLogoutVendor(rowData.id)}
          handleEdit={() => handleViewUpdateModal(rowData)}
          handleReset={() => handleResetVendorPassword(rowData.id)}
          handleTopupVendor={() => handleTopupVendor(rowData.id)}
        />
      )
    }
  ];

  const handleGetMoreData = async () => {
    const nextPage = Math.ceil(VENDORS_LIST.length / LIMIT) + 1;
    await dispatch(getVendorsAction({ page: nextPage, searchName: filterName, limit: LIMIT }));
    setDataPageFetched((prev) => nextPage);
    // await dispatch(getVendorsAction({ page: dataPageFetched + 1, searchName: filterName }));
    // setDataPageFetched((prev) => prev + 1);
  };

  const handleFilterVendors = async (values) => {

    setLoading(true);
    const response = await dispatch(getVendorsAction({ ...values, from: values.fromDate, to: values.toDate, id: values.vendorID }));
    if (response?.error === 1) {
      toast.info(`${response.message}`);
    }
    setLoading(false);
  };



  return (
    <Page title="Vendors Module ">
      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            Vendors
          </Typography>
        </Stack>


        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <FilterVendors handleFilter={handleFilterVendors} loading={loading} />
        </Stack>

        <Table
          tableHeader={TABLE_HEAD}
          data={VENDORS_LIST}
          isLoading={isLoading}
          filterField="fullname"
          tableTitle="Vendors"
          getMoreData={handleGetMoreData}
          onFilterName={handleAsyncFilterByName}
        />
      </Container>

      {/* modal */}
      <UpdateVendor
        open={isUpdateModalOpen}
        handleClose={handleCloseUpdateModal}
        rowData={currentRow}
      />
      <ViewPasswordInfo
        open={showPwdInfo}
        handleClose={handleClosePwdInfoModal}
        messageObj={currentRow}
      />
      <TopupVendor
        open={isTopupVendorOpen}
        handleClose={handleCloseTopupVendorModal}
        vendorID={selectedVendorID}
      />
    </Page>
  );
}

export default VendorsModule;
